import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ClothesQuiz.css";

import saree from "../assets/clothes/saree.png";
import bharathanatyamDanceSaree from "../assets/clothes/bharathanatyam_dance_saree.png";
import kurtha from "../assets/clothes/kurtha.png";
import vetti from "../assets/clothes/vetti.png";
import panjavi from "../assets/clothes/panjavi.png";
import kandayanOutfitMen from "../assets/clothes/kandayan_outfit_men.png";
import lungi from "../assets/clothes/lungi.png";
import osariya from "../assets/clothes/osariya.png";
import clothesBadge from "../assets/badges/clothesbadge.png";
import quizResultImg from "../assets/General/quizresult.png";

// master list of all clothing items with their display title and image
const CLOTHES = [
  { key: "saree",                      title: "Saree",              img: saree },
  { key: "bharathanatyam_dance_saree", title: "Dance Saree",        img: bharathanatyamDanceSaree },
  { key: "kurtha",                     title: "Kurtha",             img: kurtha },
  { key: "vetti",                      title: "Vetti",              img: vetti },
  { key: "panjavi",                    title: "Panjavi",            img: panjavi },
  { key: "kandayan_outfit_men",        title: "Kandyan Outfit",     img: kandayanOutfitMen },
  { key: "lungi",                      title: "Lungi",              img: lungi },
  { key: "osariya",                    title: "Osariya",            img: osariya },
];

// how many questions get asked per round
const TOTAL_QUESTIONS = 5;

// api base falls back to localhost if the env variable isnt set
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// fisher-yates shuffle, returns a new array and leaves the orignal untouched
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// picks TOTAL_QUESTIONS random clothes as answers and builds 3-option questions for each
function buildQuestions() {
  const chosenAnswers = shuffle(CLOTHES).slice(0, TOTAL_QUESTIONS);
  return chosenAnswers.map((correctItem) => {
    // grab 2 wrong options from everything except the correct item
    const wrongOptions = shuffle(
      CLOTHES.filter((item) => item.key !== correctItem.key)
    ).slice(0, 2);

    // shuffle the three options so the correct one isnt always first
    const options = shuffle([correctItem, ...wrongOptions]);

    return {
      id: correctItem.key,
      prompt: correctItem.title,
      correctKey: correctItem.key,
      options,
      selectedKey: null,
    };
  });
}

// tries a few common localStorage keys to find the logged-in players name
function getUserName() {
  const possibleKeys = [
    "exploreLankaUser",
    "exploreLankaUsername",
    "currentUser",
    "user",
    "username",
    "name",
  ];
  for (const key of possibleKeys) {
    const stored = localStorage.getItem(key);
    if (!stored) continue;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
      if (parsed?.name) return String(parsed.name).trim();
      if (parsed?.user?.name) return String(parsed.user.name).trim();
      if (parsed?.username) return String(parsed.username).trim();
    } catch {
      // if it wasnt valid json just use the raw string
      if (stored.trim()) return stored.trim();
    }
  }
  return "";
}

export default function ClothesQuiz() {
  const navigate = useNavigate();

  // plays a short cheer sound when the quiz finshes
  function playYaySound() {
    try {
      const yay = new Audio("/audio/yay.mp3");
      yay.volume = 1;
      yay.play().catch(() => {});
    } catch (error) {
      console.warn("Yay sound failed:", error);
    }
  }

  // plays a fanfare when a badge is awarded
  function playBadgeSound() {
    try {
      const badge = new Audio("/audio/badge.mp3");
      badge.volume = 1;
      badge.play().catch(() => {});
    } catch (error) {
      console.warn("Badge sound failed:", error);
    }
  }

  // false until the player clicks "start quiz" on the intro screen
  const [hasStarted, setHasStarted] = useState(false);

  // the array of question objects for this round
  const [questions, setQuestions] = useState(() => buildQuestions());

  // index of whichever question is currently on screen
  const [currentIndex, setCurrentIndex] = useState(0);

  // the key of the option the player just clicked
  const [selectedKey, setSelectedKey] = useState(null);

  // flips to true after an answer is chosen so the feedback banner shows
  const [showFeedback, setShowFeedback] = useState(false);

  // running correct-answer count shown in the top bar
  const [score, setScore] = useState(0);

  // controls whether the badge popup is visible
  const [showBadgePopup, setShowBadgePopup] = useState(false);

  // true while the post-quiz api call is in flight
  const [savingResult, setSavingResult] = useState(false);

  // best score fetched from the api on load, null means not loaded yet
  const [bestScore, setBestScore] = useState(null);

  // ref so the save effect can check it without needing it as a dependancy
  const hasSavedResultRef = useRef(false);

  // refs that track score and answers synchronusly so the save effect gets accurate data
  const scoreRef = useRef(0);
  const answersRef = useRef([]);

  const currentQuestion = questions[currentIndex];

  // once currentIndex goes past the last question the quiz is done
  const isFinished = currentIndex >= questions.length;

  // "1 / 5" style label for the top bar
  const progressText = useMemo(() => {
    return `${Math.min(currentIndex + 1, questions.length)} / ${questions.length}`;
  }, [currentIndex, questions.length]);

  // fetch the players personal best before the intro screen is shown
  useEffect(() => {
    if (hasStarted) return;

    async function fetchBestScore() {
      const playerName = getUserName();
      if (!playerName) return;

      try {
        const url = `${API_BASE}/api/best-score/${encodeURIComponent(playerName)}/clothing_quiz`;
        const res = await fetch(url);
        const data = await res.json();
        if (data?.best_score !== null && data?.best_score !== undefined) {
          setBestScore(data.best_score);
        }
      } catch (err) {
        console.warn("[ClothesQuiz] fetchBestScore error:", err);
      }
    }

    fetchBestScore();
  }, [hasStarted]);

  // resets all state and starts a fresh round
  function startQuiz() {
    setQuestions(buildQuestions());
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
    setHasStarted(true);
  }

  // called when the player taps an answer card
  function handleAnswer(optionKey) {
    // ignore extra clicks while feedback is already showing
    if (showFeedback) return;

    const isCorrect = optionKey === currentQuestion.correctKey;

    // update the ref straight away so the save effect sees the right value
    if (isCorrect) scoreRef.current += 1;

    // record this answer for the api payload
    answersRef.current = [
      ...answersRef.current,
      {
        question: currentQuestion.prompt,
        correct_key: currentQuestion.correctKey,
        selected_key: optionKey,
        was_correct: isCorrect,
      },
    ];

    setSelectedKey(optionKey);
    setShowFeedback(true);

    // also stamp the selected key onto the question object for the results screen
    setQuestions((prev) =>
      prev.map((question, index) =>
        index === currentIndex ? { ...question, selectedKey: optionKey } : question
      )
    );
    if (isCorrect) setScore((prev) => prev + 1);
  }

  // advances to the next question and clears the feedback state
  function handleNext() {
    setCurrentIndex((prev) => prev + 1);
    if (currentIndex < questions.length - 1) {
      setSelectedKey(null);
      setShowFeedback(false);
    }
  }

  // goes back to the intro screen and wipes all round state
  function handlePlayAgain() {
    setHasStarted(false);
    setQuestions(buildQuestions());
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
  }

  // fires once when the last question is answered and sends the result to the api
  useEffect(() => {
    async function saveQuizResult() {
      if (!isFinished || hasSavedResultRef.current) return;

      hasSavedResultRef.current = true;
      setSavingResult(true);
      playYaySound();

      const finalScore = scoreRef.current;
      const finalAnswers = answersRef.current;
      const playerName = getUserName();

      if (!playerName) {
        console.warn("[ClothesQuiz] no player name found, score not saved.");
        setSavingResult(false);
        return;
      }

      try {
        const payload = {
          name: playerName,
          page_name: "clothing_quiz",
          score: finalScore,
          total_questions: questions.length,
          badge_key: "clothes_master",
          answers: finalAnswers,
        };

        const response = await fetch(`${API_BASE}/api/quiz-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok) {
          console.error("[ClothesQuiz] save failed:", data);
          return;
        }

        // update the displayed best score if the api returns a new one
        if (data?.best_score !== null && data?.best_score !== undefined) {
          setBestScore(data.best_score);
        }

        // if this run unlocked a badge show the popup after a short delay
        if (data?.newly_awarded_badge === true) {
          playBadgeSound();
          setTimeout(() => setShowBadgePopup(true), 500);
        }

      } catch (error) {
        console.error("[ClothesQuiz] network error:", error);
      } finally {
        setSavingResult(false);
      }
    }

    saveQuizResult();
  }, [isFinished, questions.length]);

  // intro screen shown before the player starts
  if (!hasStarted) {
    return (
      <div className="clothesQuizPage">
        <header className="clothesQuizHeader">
          <div className="clothesQuizHeaderTop">
            <button
              className="quizHomeBtn"
              onClick={() => navigate("/loading", { state: { to: "/home" } })}
              aria-label="Go Home"
              title="Go Home"
              type="button"
            >
              {/* inline svg so the fill colour is easy to override in css */}
              <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="quizHomeBtn__icon"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                  fill="#ffffff"
                />
              </svg>
            </button>
          </div>
          <h1 className="clothesQuizTitle">Clothes Quiz Adventure!</h1>
          <p className="clothesQuizSubtitle">
            Let's see how well you know Sri Lankan clothing!
          </p>
        </header>

        <main className="clothesQuizWrap">
          <section className="introPanel">
            {/* only render the best score line if the api has returned a value */}
            {bestScore !== null && (
              <p className="introBestScore">
                Your best score: <strong>{bestScore} / {TOTAL_QUESTIONS}</strong>
              </p>
            )}

            <h2 className="introTitle">How to play</h2>

            <div className="introSteps">
              <div className="introStep">
                <span className="introStepNumber">1</span>
                <p>Read the clothing name at the top.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">2</span>
                <p>Look at the 3 picture choices.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">3</span>
                <p>Tap the picture that matches the name.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">4</span>
                <p>Get 5 out of 5 to win the special clothes badge!</p>
              </div>
            </div>

            <div className="introActions">
              <button className="startQuizBtn" onClick={startQuiz} type="button">
                Start Quiz
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  // results screen shown after the last question is answered
  if (isFinished) {
    return (
      <div className="clothesQuizPage">
        <header className="clothesQuizHeader">
          <div className="clothesQuizHeaderTop">
            <button
              className="quizHomeBtn"
              onClick={() => navigate("/loading", { state: { to: "/home" } })}
              aria-label="Go Home"
              title="Go Home"
              type="button"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="quizHomeBtn__icon"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                  fill="#ffffff"
                />
              </svg>
            </button>
          </div>
          <h1 className="clothesQuizTitle">Clothes Quiz Adventure!</h1>
          <p className="clothesQuizSubtitle">Amazing work, explorer!</p>
        </header>

        <main className="clothesQuizWrap">
          <section className="resultPanel">
            <img
              src={quizResultImg}
              alt="Quiz result"
              className="quizResultImage"
            />
            <h2 className="quizResultTitle">You finished the quiz!</h2>

            {/* count correct answers directly from the question objects */}
            <p className="quizResultScore">
              Your score:{" "}
              <span>{questions.filter((q) => q.selectedKey === q.correctKey).length}</span>
              {" "}/ {questions.length}
            </p>

            {savingResult && (
              <p className="quizSavingText">Saving your score...</p>
            )}

            <div className="quizResultActions">
              <button className="quizActionBtn" onClick={handlePlayAgain} type="button">
                Play Again
              </button>
              <button
                className="quizActionBtn secondary"
                onClick={() => navigate("/clothing")}
                type="button"
              >
                Back to Clothing Game
              </button>
            </div>
          </section>
        </main>

        {/* badge popup appears on top of everthing if the player earned it */}
        {showBadgePopup && (
          <div className="badgeOverlay" onClick={() => setShowBadgePopup(false)}>
            <div className="badgePopup" onClick={(e) => e.stopPropagation()}>
              <h2 className="badgePopupTitle">Badge Unlocked!</h2>
              <p className="badgePopupText">
                You got 5 out of 5 and earned the Clothes Badge!
              </p>
              <img src={clothesBadge} alt="Clothes badge" className="badgeImage" />
              <button
                className="quizActionBtn"
                onClick={() => setShowBadgePopup(false)}
                type="button"
              >
                Yay!
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // main question screen
  return (
    <div className="clothesQuizPage">
      <header className="clothesQuizHeader">
        <div className="clothesQuizHeaderTop">
          <button
            className="quizHomeBtn"
            onClick={() => navigate("/loading", { state: { to: "/home" } })}
            aria-label="Go Home"
            title="Go Home"
            type="button"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="quizHomeBtn__icon"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                fill="#ffffff"
              />
            </svg>
          </button>
        </div>
        <h1 className="clothesQuizTitle">Clothes Quiz Adventure!</h1>
        <p className="clothesQuizSubtitle">
          Pick the correct picture for the clothing name.
        </p>
      </header>

      {/* progress and score pills */}
      <div className="clothesQuizTopBar">
        <span className="quizPill">Question: <b>{progressText}</b></span>
        <span className="quizPill">Score: <b>{score}</b></span>
      </div>

      <main className="clothesQuizWrap">
        <section className="clothesQuestionPanel">
          <p className="quizPromptLabel">Can you find this?</p>

          {/* the clothing name the player needs to match to a picture */}
          <h2 className="quizPromptName">{currentQuestion.prompt}</h2>

          <div className="clothesQuizOptionsGrid">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedKey === option.key;
              const isCorrect = showFeedback && option.key === currentQuestion.correctKey;
              const isWrong =
                showFeedback && isSelected && option.key !== currentQuestion.correctKey;

              return (
                <button
                  key={option.key}
                  className={`clothesQuizOptionCard ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isWrong ? "wrong" : ""}`}
                  onClick={() => handleAnswer(option.key)}
                  type="button"
                  disabled={showFeedback}
                >
                  <div className="clothesQuizOptionImageWrap">
                    <img
                      src={option.img}
                      alt={option.title}
                      className="clothesQuizOptionImage"
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* feedback banner shows after the player picks an answer */}
          {showFeedback && (
            <div
              className={`clothesQuizFeedback ${
                selectedKey === currentQuestion.correctKey ? "good" : "bad"
              }`}
            >
              {selectedKey === currentQuestion.correctKey ? "Correct! Great job!" : "Oops!"}
            </div>
          )}

          {/* next button only appears once an answer has been submitted */}
          {showFeedback && (
            <div className="quizNextRow">
              <button className="quizActionBtn" onClick={handleNext} type="button">
                {currentIndex === questions.length - 1 ? "See Results" : "Next Question"}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}